/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/ui/DateRangePicker.tsx
 🗓️ Created: 2026-03-20
 🧩 Description:
     Compact single-trigger date range picker.
     Shows Start → End in one button, opens a popover with
     preset shortcuts and custom date inputs.
     No external date library required.
────────────────────────────────────────────────────────────────*/

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronDown } from 'lucide-react';


export interface DateRange {
  startDate: string; // YYYY-MM-DD
  endDate:   string;
  startTime?: string; // HH:mm (optional, defaults to 00:00)
  endTime?:   string; // HH:mm (optional, defaults to 23:59)
}

interface Preset {
  label: string;
  getDates: () => DateRange;
}

interface Props {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
  disabled?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toISO(d: Date): string {
  return d.toISOString().split('T')[0];
}

function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function daysAgo(n: number): Date {
  const d = today();
  d.setDate(d.getDate() - n);
  return d;
}

function startOfMonth(d: Date = today()): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date = today()): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function startOfQuarter(): Date {
  const t = today();
  const q = Math.floor(t.getMonth() / 3);
  return new Date(t.getFullYear(), q * 3, 1);
}

function formatDisplay(date: string, time?: string): string {
  if (!date) return '';
  const [y, m, d] = date.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const datePart = `${months[parseInt(m) - 1]} ${parseInt(d)}, ${y}`;
  if (time && time !== '00:00' && time !== '23:59') return `${datePart} ${time}`;
  return datePart;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function DateRangePicker({ value, onChange, className = '', disabled = false }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, useRight: false, right: 0, width: 0 });

  // Sync draft with external value when closed
  useEffect(() => {
    if (!open) setDraft(value);
  }, [value, open]);

  // Calculate position from trigger rect (viewport-aware)
  const calcPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const POPOVER_H = 380;
    const overflowRight = rect.left + rect.width > window.innerWidth;
    const overflowBottom = rect.bottom + 4 + POPOVER_H > window.innerHeight;
    setPopoverPos({
      top: overflowBottom ? Math.max(4, rect.top - POPOVER_H - 4) : rect.bottom + 4,
      left: overflowRight ? 0 : rect.left,
      useRight: overflowRight,
      right: overflowRight ? window.innerWidth - rect.right : 0,
      width: rect.width,
    });
  }, []);

  // Handle open/close and reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const popoverEl = document.getElementById('drp-popover');
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        !(popoverEl && popoverEl.contains(e.target as Node))
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', calcPosition, true);
    window.addEventListener('resize', calcPosition);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', calcPosition, true);
      window.removeEventListener('resize', calcPosition);
    };
  }, [open, calcPosition]);

  const applyDraft = useCallback(() => {
    if (draft.startDate && draft.endDate) {
      const start = draft.startDate <= draft.endDate ? draft.startDate : draft.endDate;
      const end   = draft.startDate <= draft.endDate ? draft.endDate   : draft.startDate;
      onChange({
        startDate: start,
        endDate:   end,
        startTime: draft.startTime || '00:00',
        endTime:   draft.endTime   || '23:59',
      });
      setOpen(false);
    }
  }, [draft, onChange]);

  const applyPreset = useCallback((range: DateRange) => {
    onChange({ ...range, startTime: '00:00', endTime: '23:59' });
    setOpen(false);
  }, [onChange]);

  const PRESETS: Preset[] = [
    { label: 'Today',           getDates: () => ({ startDate: toISO(today()),          endDate: toISO(today()) }) },
    { label: 'Last 7 days',     getDates: () => ({ startDate: toISO(daysAgo(6)),       endDate: toISO(today()) }) },
    { label: 'Last 30 days',    getDates: () => ({ startDate: toISO(daysAgo(29)),      endDate: toISO(today()) }) },
    { label: 'Last 90 days',    getDates: () => ({ startDate: toISO(daysAgo(89)),      endDate: toISO(today()) }) },
    { label: 'This month',      getDates: () => ({ startDate: toISO(startOfMonth()),   endDate: toISO(endOfMonth()) }) },
    { label: 'Last month',      getDates: () => {
        const last = new Date(today().getFullYear(), today().getMonth() - 1, 1);
        return { startDate: toISO(startOfMonth(last)), endDate: toISO(endOfMonth(last)) };
    }},
    { label: 'This quarter',    getDates: () => ({ startDate: toISO(startOfQuarter()), endDate: toISO(today()) }) },
    { label: 'This year',       getDates: () => ({ startDate: `${today().getFullYear()}-01-01`, endDate: toISO(today()) }) },
  ];

  const displayLabel = value.startDate && value.endDate
    ? `${formatDisplay(value.startDate, value.startTime)}  →  ${formatDisplay(value.endDate, value.endTime)}`
    : 'Select date range';


  return (
    <div ref={containerRef} className={`relative inline-block ${className}`}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (disabled) return;
          if (!open) calcPosition(); // position BEFORE rendering
          setOpen(o => !o);
        }}
        disabled={disabled}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg border border-input bg-background
          text-sm text-foreground hover:border-primary/60 hover:bg-muted/30
          focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors
          disabled:opacity-50 disabled:cursor-not-allowed w-full
        `}
      >
        <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="flex-1 text-left font-medium">{displayLabel}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Popover — via Portal to escape stacking context (backdrop-blur, transform, overflow) */}
      {open && createPortal(
        <div
          id="drp-popover"
          className="fixed z-[9999] bg-card border border-border rounded-xl shadow-2xl flex flex-row overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150"
          style={{
            top: popoverPos.top,
            ...(popoverPos.useRight
              ? { right: popoverPos.right, left: 'auto' }
              : { left: popoverPos.left }),
            minWidth: Math.max(popoverPos.width, 350) || undefined,
          }}
        >
          {/* Presets column — izquierda */}
          <div className="flex-1 border-r border-border p-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1.5">
              Quick Select
            </p>
            {PRESETS.map(p => {
              const range = p.getDates();
              const isActive = range.startDate === value.startDate && range.endDate === value.endDate;
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => applyPreset(range)}
                  className={`
                    w-full whitespace-nowrap text-left text-sm px-3 py-1.5 rounded-lg transition-colors
                    ${isActive
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'text-foreground hover:bg-muted'
                    }
                  `}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          {/* Custom range column — derecha */}
          <div className="flex-1 p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Custom Range
            </p>

            <div className="space-y-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">From</label>
                <div className="relative flex items-center">
                  <input
                    type="date"
                    value={draft.startDate}
                    max={draft.endDate || undefined}
                    onChange={e => setDraft(d => ({ ...d, startDate: e.target.value }))}
                    className="w-full pl-3 pr-10 py-2 text-sm border border-input bg-background text-foreground rounded-lg
                               focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                               [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                  <Calendar className="absolute right-3 w-4 h-4 text-muted-foreground pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Start time</label>
                <div className="flex items-center justify-center gap-0.5 w-24 px-2 py-2 text-sm border border-input bg-background text-foreground rounded-lg focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary">
                  <select
                    value={draft.startTime ? draft.startTime.split(':')[0] : '00'}
                    onChange={e => setDraft(d => ({ ...d, startTime: `${e.target.value}:${d.startTime?.split(':')[1] || '00'}` }))}
                    className="bg-transparent outline-none cursor-pointer appearance-none text-center"
                  >
                    {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span className="text-muted-foreground font-bold">:</span>
                  <select
                    value={draft.startTime ? draft.startTime.split(':')[1] : '00'}
                    onChange={e => setDraft(d => ({ ...d, startTime: `${d.startTime?.split(':')[0] || '00'}:${e.target.value}` }))}
                    className="bg-transparent outline-none cursor-pointer appearance-none text-center"
                  >
                    {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">To</label>
                <div className="relative flex items-center">
                  <input
                    type="date"
                    value={draft.endDate}
                    min={draft.startDate || undefined}
                    onChange={e => setDraft(d => ({ ...d, endDate: e.target.value }))}
                    className="w-full pl-3 pr-10 py-2 text-sm border border-input bg-background text-foreground rounded-lg
                               focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                               [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                  <Calendar className="absolute right-3 w-4 h-4 text-muted-foreground pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">End time</label>
                <div className="flex items-center justify-center gap-0.5 w-24 px-2 py-2 text-sm border border-input bg-background text-foreground rounded-lg focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary">
                  <select
                    value={draft.endTime ? draft.endTime.split(':')[0] : '23'}
                    onChange={e => setDraft(d => ({ ...d, endTime: `${e.target.value}:${d.endTime?.split(':')[1] || '59'}` }))}
                    className="bg-transparent outline-none cursor-pointer appearance-none text-center"
                  >
                    {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span className="text-muted-foreground font-bold">:</span>
                  <select
                    value={draft.endTime ? draft.endTime.split(':')[1] : '59'}
                    onChange={e => setDraft(d => ({ ...d, endTime: `${d.endTime?.split(':')[0] || '23'}:${e.target.value}` }))}
                    className="bg-transparent outline-none cursor-pointer appearance-none text-center"
                  >
                    {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyDraft}
                disabled={!draft.startDate || !draft.endDate}
                className="flex-1 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-lg
                           hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
